<script setup lang="ts">
import { computed, ref, type Component } from 'vue';
import { useIndmoRichTextIcons } from '@/composables/useIndmoRichTextIcons';
import { normalizeWagtailRichTextHtml } from '@/utils/html';
import { onOfflineMediaImgError } from '@/utils/offlineMediaPlaceholder';
import {
  Home,
  Info,
  Building2,
  FileCheck,
  BookOpen,
  Megaphone,
  GraduationCap,
  Briefcase,
  Users,
  Quote,
} from 'lucide-vue-next';
import * as LucideIcons from 'lucide-vue-next';

const BuildingIcon = (LucideIcons as Record<string, Component>).Building;
const MessageSquareIcon =
  (LucideIcons as Record<string, Component>).MessageSquare;
const FileTextIcon: Component =
  (LucideIcons as Record<string, Component>).FileText ??
  (LucideIcons as Record<string, Component>).FileTextIcon ??
  FileCheck;
const NewspaperIcon: Component =
  (LucideIcons as Record<string, Component>).Newspaper ?? FileTextIcon;

const ICON_MAP: Record<string, Component> = {
  home: Home,
  info: Info,
  building: (BuildingIcon ?? Building2) as Component,
  'building-2': Building2,
  'file-check': FileCheck,
  'file-text': FileTextIcon,
  'book-open': BookOpen,
  'message-square': MessageSquareIcon ?? Info,
  megaphone: Megaphone,
  'graduation-cap': GraduationCap,
  briefcase: Briefcase,
  users: Users,
  newspaper: NewspaperIcon,
  quote: Quote,
};

type NewsSummaryDisplayItem = {
  id: number;
  title: string;
  richTitle?: string | null;
  href: string;
  /** ISO date string for `<time datetime>` when present */
  dateIso?: string | null;
  dateLabel?: string | null;
  categoryName?: string | null;
  imageUrl?: string | null;
  imageAlt?: string;
};

type NewsSummaryDisplayColumn = {
  title: string;
  iconKey: string;
  items: NewsSummaryDisplayItem[];
};

const props = defineProps<{
  /** Resolved columns, or a JSON string (admin block preview query param). */
  columns?: NewsSummaryDisplayColumn[] | string | null;
}>();

const columnsResolved = computed((): NewsSummaryDisplayColumn[] => {
  const raw = props.columns;
  if (raw == null) return [];
  if (typeof raw === 'string') {
    const s = raw.trim();
    if (!s) return [];
    try {
      const parsed = JSON.parse(s) as unknown;
      return Array.isArray(parsed) ? (parsed as NewsSummaryDisplayColumn[]) : [];
    } catch {
      return [];
    }
  }
  return raw;
});

function iconForKey(key: string): Component {
  const c = ICON_MAP[key] ?? NewspaperIcon;
  return c;
}

const summaryRootEl = ref<HTMLElement | null>(null);
useIndmoRichTextIcons(summaryRootEl, () =>
  columnsResolved.value
    .map((c) => c.items.map((i) => normalizeWagtailRichTextHtml(i.richTitle)).join('\u0000'))
    .join('|')
);
</script>

<template>
  <div
    v-if="columnsResolved.length"
    ref="summaryRootEl"
    class="news-summary"
    role="region"
    :aria-label="columnsResolved.map((c) => c.title).join(', ')"
  >
    <div class="news-summary__grid">
      <div
        v-for="(col, colIdx) in columnsResolved"
        :key="`${col.title}-${colIdx}`"
        class="news-summary__card"
      >
        <div class="news-summary__card-head">
          <div class="news-summary__icon-wrap" aria-hidden="true">
            <component :is="iconForKey(col.iconKey)" class="news-summary__icon" />
          </div>
          <h3 class="news-summary__card-title">{{ col.title }}</h3>
        </div>

        <ul v-if="col.items.length" class="news-summary__list">
          <li
            v-for="item in col.items"
            :key="`${item.id}-${item.href}`"
            class="news-summary__row"
          >
            <a :href="item.href" class="news-summary__link">
              <div
                v-if="item.imageUrl"
                class="news-summary__media"
              >
                <img
                  :src="item.imageUrl"
                  :alt="item.imageAlt ?? ''"
                  class="news-summary__media-img"
                  loading="lazy"
                  width="160"
                  height="120"
                  @error="onOfflineMediaImgError"
                />
              </div>
              <div class="news-summary__body">
                <div
                  v-if="item.richTitle && String(item.richTitle).trim()"
                  class="news-summary__item-title news-summary__item-title--rich block-rich-text"
                  v-html="normalizeWagtailRichTextHtml(item.richTitle)"
                />
                <span v-else class="news-summary__item-title">{{ item.title }}</span>
                <div class="news-summary__meta">
                  <span v-if="item.categoryName" class="news-summary__badge">{{
                    item.categoryName
                  }}</span>
                  <time
                    v-if="item.dateLabel"
                    class="news-summary__date"
                    :datetime="item.dateIso ?? undefined"
                    >{{ item.dateLabel }}</time
                  >
                </div>
              </div>
            </a>
          </li>
        </ul>
        <p v-else class="news-summary__empty">No news to show yet.</p>
      </div>
    </div>
  </div>
</template>

<style scoped>
.news-summary {
  width: 100%;
}

.news-summary__grid {
  display: grid;
  grid-template-columns: 1fr;
  gap: 1.5rem;
}

@media (min-width: 1024px) {
  .news-summary__grid {
    grid-template-columns: repeat(auto-fit, minmax(17.5rem, 1fr));
    gap: 2rem;
  }
}

.news-summary__card {
  display: flex;
  flex-direction: column;
  min-height: 0;
  padding: 1.5rem;
  background: var(--color-surface, #fff);
  border: 1px solid var(--color-border, #e5e7eb);
  border-radius: 0.75rem;
  transition: box-shadow 0.3s ease;
}

@media (min-width: 640px) {
  .news-summary__card {
    padding: 2rem;
    border-radius: 1rem;
  }
}

.news-summary__card:hover {
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.06);
}

.news-summary__card-head {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  margin-bottom: 1.25rem;
}

.news-summary__icon-wrap {
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 3rem;
  height: 3rem;
  border-radius: 6px;
  background: color-mix(in srgb, var(--color-primary, #047857) 10%, transparent);
}

.news-summary__icon {
  width: 1.5rem;
  height: 1.5rem;
  color: var(--color-primary, #047857);
}

.news-summary__card-title {
  margin: 0;
  font-size: 1.25rem;
  font-weight: 700;
  line-height: 1.25;
  color: var(--color-text, #1a1a1a);
  font-family: var(--font-family-heading, inherit);
}

.news-summary__list {
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 1.25rem;
}

.news-summary__row {
  margin: 0;
}

.news-summary__link {
  display: flex;
  gap: 1rem;
  text-decoration: none;
  color: inherit;
  border-radius: 0.5rem;
  outline-offset: 2px;
}

.news-summary__link:hover .news-summary__item-title {
  color: var(--color-primary, #047857);
}

.news-summary__media {
  flex-shrink: 0;
  width: 5rem;
  height: 4rem;
  border-radius: 0.375rem;
  overflow: hidden;
  background: var(--color-border, #e5e7eb);
}

.news-summary__media-img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.news-summary__body {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 0.35rem;
}

.news-summary__item-title {
  margin: 0;
  font-size: 0.9375rem;
  font-weight: 600;
  line-height: 1.35;
  color: var(--color-text, #1a1a1a);
  transition: color 0.2s ease;
}

.news-summary__item-title--rich :deep(p) {
  margin: 0;
  display: inline;
}

.news-summary__item-title--rich :deep(.indmo-richtext-icon) {
  display: inline-flex;
  vertical-align: -0.125em;
  margin-inline-end: 0.15em;
  color: var(--color-primary);
}

.news-summary__item-title--rich :deep(.indmo-richtext-icon svg) {
  width: 1em;
  height: 1em;
}

.news-summary__meta {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.8125rem;
  color: var(--color-text-muted, #6b7280);
}

.news-summary__badge {
  display: inline-block;
  padding: 0.125rem 0.5rem;
  border-radius: 999px;
  background: color-mix(in srgb, var(--color-primary, #047857) 12%, transparent);
  color: var(--color-text, #374151);
  font-weight: 500;
}

.news-summary__date {
  font-variant-numeric: tabular-nums;
}

.news-summary__empty {
  margin: 0;
  font-size: 0.875rem;
  color: var(--color-text-muted, #6b7280);
}
</style>
